import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectClassroom } from '../Classrooms';
import { getMiddleSchoolUnit, submitAttendance } from '../BackendInterface';
import { getLocalForage, removeLocalForage, setLocalForage } from '../aaa';
import { RootState } from '../ApplicationState';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { Lesson, UnitResult } from '@peekapak/types/LessonPlanTypes';
import {
  AttendanceDraft,
  AttendanceStatus,
  buildAttendanceCacheKey,
  buildSessionObjects,
  buildSubmissionPayload,
  countAttendance,
  initializeAttendanceStatuses,
} from './attendanceHelpers';

interface UrlParams {
  unitId: string;
  language: string;
  grade: string;
}

const PRIMARY_BUTTON_CLASS =
  "rounded-[0.5rem] border-0 bg-[#00afd8] px-6 py-[1.6rem] font-['GT-Walsheim-Pro-Bold'] text-[1.6rem] uppercase text-white transition-[background_250ms_ease-in-out,transform_150ms_ease] hover:bg-[#009fc4] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40 active:bg-[#0290b2]";

const SECONDARY_BUTTON_CLASS =
  "rounded-[0.5rem] border-2 border-[#00afd8] bg-white px-6 py-[1.6rem] font-['GT-Walsheim-Pro-Bold'] text-[1.6rem] uppercase text-[#00afd8] transition-[background_250ms_ease-in-out] hover:bg-[#e6f9fd] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40";

const PRIMARY_BUTTON_SMALL_CLASS =
  "rounded-[0.5rem] border-0 bg-[#00afd8] px-5 py-2 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase text-white transition-[background_250ms_ease-in-out,transform_150ms_ease] hover:bg-[#009fc4] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40 active:bg-[#0290b2]";

const SECONDARY_BUTTON_SMALL_CLASS =
  "rounded-[0.5rem] border-2 border-[#00afd8] bg-white px-5 py-2 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase text-[#00afd8] transition-[background_250ms_ease-in-out] hover:bg-[#e6f9fd] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40";

// ---------------------------------------------------------------------------
// Student card sub-component
// ---------------------------------------------------------------------------

interface StudentCardProps {
  student: StudentProxyType;
  status: AttendanceStatus;
  onToggle: (studentUserId: string) => void;
}

function StudentCard({ student, status, onToggle }: StudentCardProps) {
  const isPresent = status === 'present';
  return (
    <button
      type='button'
      onClick={() => onToggle(student.userId)}
      className={[
        'rounded-lg border-2 p-4 text-left cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 w-full',
        isPresent
          ? 'bg-blue-100 border-blue-300 hover:bg-blue-200 focus:ring-blue-400'
          : 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200 focus:ring-yellow-400',
      ].join(' ')}
      aria-pressed={isPresent}
      aria-label={`${student.firstName}${student.lastName ? ` ${student.lastName}` : ''} — ${isPresent ? 'Present' : 'Absent'}. Click to toggle.`}
    >
      <span className='block font-semibold text-gray-800 text-[1.75rem] leading-snug'>
        {student.firstName}
        {student.lastName ? ` ${student.lastName}` : ''}
      </span>
      <span
        className={['block text-[1.5rem] mt-1 font-medium', isPresent ? 'text-blue-600' : 'text-yellow-600'].join(' ')}
      >
        {isPresent ? 'Present' : 'Absent'}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TakweenAttendancePage = (): JSX.Element => {
  const { unitId, language, grade } = useParams<UrlParams>();
  const history = useHistory();
  // MainLayout guarantees classrooms are loaded before rendering child routes
  const classroom = useSelector((state: RootState) => selectClassroom(state));

  // Form state
  const [shiftNumber, setShiftNumber] = useState<string>('');
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [attendanceStatuses, setAttendanceStatuses] = useState<Record<string, AttendanceStatus>>({});

  // Unit data (for display name + lesson list)
  const [unitDisplayName, setUnitDisplayName] = useState<string>('');
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoadingUnit, setIsLoadingUnit] = useState(true);

  // Overlay state
  const [showConfirmOverlay, setShowConfirmOverlay] = useState(false);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------------------------------------------------------------------
  // On mount: load unit data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadUnit() {
      setIsLoadingUnit(true);
      try {
        // BackendInterface types getMiddleSchoolUnit as Promise<Unit> but actually returns UnitResult at runtime
        const unitPayload = (await getMiddleSchoolUnit(unitId, language, grade)) as unknown as UnitResult;
        if (cancelled) return;
        const unit = unitPayload.data;
        setUnitDisplayName(unit.displayName ?? '');
        const sortedLessons = [...(unit.lessons?.content ?? [])].sort((a, b) => a.index - b.index);
        setLessons(sortedLessons);
      } catch (err) {
        console.error('TakweenAttendancePage: failed to load unit', err);
      }
      if (!cancelled) setIsLoadingUnit(false);
    }
    loadUnit();
    return () => {
      cancelled = true;
    };
  }, [unitId, language, grade]);

  // Keep latest form state available without retriggering the classroom-switch effect.
  const formStateRef = useRef({
    shiftNumber,
    selectedSessions,
    attendanceStatuses,
  });
  useEffect(() => {
    formStateRef.current = {
      shiftNumber,
      selectedSessions,
      attendanceStatuses,
    };
  }, [shiftNumber, selectedSessions, attendanceStatuses]);

  // ---------------------------------------------------------------------------
  // On classroom change:
  // 1) silently preserve current classroom draft,
  // 2) restore incoming classroom draft (or initialize defaults).
  // ---------------------------------------------------------------------------
  const previousClassroomIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!classroom?.listOfStudentProxies || !classroom.classroomId) return;

    const incomingClassroomId = classroom.classroomId;
    const outgoingClassroomId = previousClassroomIdRef.current;
    if (incomingClassroomId === outgoingClassroomId) return;

    let cancelled = false;

    async function initStudents() {
      if (outgoingClassroomId) {
        const current = formStateRef.current;
        const outgoingDraft: AttendanceDraft = {
          shiftNumber: current.shiftNumber,
          selectedSessions: buildSessionObjects(current.selectedSessions, lessons),
          attendanceStatuses: current.attendanceStatuses,
        };
        await setLocalForage(buildAttendanceCacheKey(outgoingClassroomId), outgoingDraft);
      }

      previousClassroomIdRef.current = incomingClassroomId;

      const sorted = [...classroom!.listOfStudentProxies].sort(
        (a, b) => a.firstName.localeCompare(b.firstName) || (a.lastName ?? '').localeCompare(b.lastName ?? ''),
      );
      const initialStatuses = initializeAttendanceStatuses(sorted);

      const cacheKey = buildAttendanceCacheKey(incomingClassroomId);
      const draft = (await getLocalForage(cacheKey)) as AttendanceDraft | null;
      if (cancelled) return;

      if (draft) {
        setShiftNumber(draft.shiftNumber);
        setSelectedSessions(new Set(draft.selectedSessions.map((s) => s.lessonId)));
        setAttendanceStatuses(draft.attendanceStatuses);
        await removeLocalForage(cacheKey);
      } else {
        setShiftNumber('');
        setSelectedSessions(new Set());
        setAttendanceStatuses(initialStatuses);
      }
    }
    initStudents();

    return () => {
      cancelled = true;
    };
  }, [classroom]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const sortedStudents: StudentProxyType[] = [...(classroom?.listOfStudentProxies ?? [])].sort(
    (a, b) => a.firstName.localeCompare(b.firstName) || (a.lastName ?? '').localeCompare(b.lastName ?? ''),
  );

  const attendanceCounts = countAttendance(attendanceStatuses);
  const selectedSessionObjects = buildSessionObjects(selectedSessions, lessons);
  const isShiftNumberValid = /^\d+$/.test(shiftNumber) && parseInt(shiftNumber, 10) >= 1;
  const hasSelectedSessions = selectedSessionObjects.length > 0;
  const isSubmitDisabled = isSubmitting || !isShiftNumberValid || !hasSelectedSessions;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleToggleSession(lessonId: string) {
    setSelectedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
      }
      return next;
    });
  }

  function handleToggleStudent(studentUserId: string) {
    setAttendanceStatuses((prev) => ({
      ...prev,
      [studentUserId]: (prev[studentUserId] ?? 'present') === 'present' ? 'absent' : 'present',
    }));
  }

  function handleCancel() {
    history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
  }

  async function handleSave() {
    if (classroom?.classroomId) {
      const draft: AttendanceDraft = {
        shiftNumber,
        selectedSessions: buildSessionObjects(selectedSessions, lessons),
        attendanceStatuses,
      };
      await setLocalForage(buildAttendanceCacheKey(classroom.classroomId), draft);
    }
    history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
  }

  function handleSubmitClick() {
    if (isSubmitDisabled) return;
    setShowConfirmOverlay(true);
  }

  function handleConfirmCancel() {
    setShowConfirmOverlay(false);
  }

  async function handleConfirmSubmit() {
    if (!classroom?.classroomId) return;
    setIsSubmitting(true);
    setShowConfirmOverlay(false);
    try {
      const payload = buildSubmissionPayload(
        classroom.classroomId,
        unitId,
        shiftNumber,
        selectedSessions,
        attendanceStatuses,
        lessons,
      );
      if (payload.sessions.length === 0) {
        throw new Error('Please select at least one session.');
      }
      await submitAttendance(payload);
      setSubmissionError(null);
      setShowResultOverlay(true);
      setTimeout(() => {
        history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unknown error occurred.';
      setSubmissionError(message);
      setShowResultOverlay(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleResultOkay() {
    setShowResultOverlay(false);
    setSubmissionError(null);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className='min-h-screen bg-gray-50 text-gray-900'>
      {/* Loading state */}
      {isLoadingUnit && (
        <div className='flex items-center justify-center py-20'>
          <p className='text-gray-500 text-[2.25rem]'>Loading…</p>
        </div>
      )}

      {!isLoadingUnit && (
        <>
          {/* Page header */}
          <header className='bg-white border-b border-gray-200 px-6 py-5'>
            <h1 className='text-[3rem] font-bold text-gray-900 mb-4'>{unitDisplayName}</h1>

            <div className='flex flex-wrap gap-6 items-start'>
              {/* Shift # */}
              <div className='flex flex-col gap-1'>
                <label htmlFor='shift-number' className='text-[1.75rem] font-semibold text-gray-700'>
                  Shift #
                </label>
                <input
                  id='shift-number'
                  type='number'
                  min='1'
                  step='1'
                  value={shiftNumber}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || (/^\d+$/.test(raw) && parseInt(raw, 10) >= 1)) {
                      setShiftNumber(raw);
                    }
                  }}
                  className='w-32 rounded-md border border-gray-300 px-3 py-1.5 text-[1.75rem] focus:outline-none focus:ring-2 focus:ring-blue-400'
                  placeholder='e.g. 1'
                />
              </div>

              {/* Sessions */}
              <fieldset className='flex flex-col gap-1'>
                <legend className='text-[1.75rem] font-semibold text-gray-700 mb-1'>Sessions</legend>
                <div className='grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2'>
                  {lessons.map((lesson, idx) => {
                    const checkboxId = `session-checkbox-${lesson.lessonId}`;
                    return (
                      <label
                        key={lesson.lessonId}
                        htmlFor={checkboxId}
                        className='flex cursor-pointer items-center gap-2 text-[1.75rem]'
                      >
                        <input
                          id={checkboxId}
                          type='checkbox'
                          checked={selectedSessions.has(lesson.lessonId)}
                          onChange={() => handleToggleSession(lesson.lessonId)}
                          className='h-4 w-4 shrink-0 rounded border-gray-400 text-blue-500 focus:ring-blue-400'
                        />
                        <span>
                          Session {idx + 1}: {lesson.title}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          </header>

          {/* Student card grid */}
          <main className='px-6 py-6'>
            {sortedStudents.length === 0 ? (
              <p className='text-gray-500 text-[1.75rem]'>No students found in this classroom.</p>
            ) : (
              <div className='grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'>
                {sortedStudents.map((student) => (
                  <StudentCard
                    key={student.sid}
                    student={student}
                    status={attendanceStatuses[student.userId] ?? 'present'}
                    onToggle={handleToggleStudent}
                  />
                ))}
              </div>
            )}
          </main>

          {/* Footer action bar */}
          <footer className='sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end'>
            <button type='button' onClick={handleCancel} className={SECONDARY_BUTTON_SMALL_CLASS}>
              Cancel
            </button>
            <button type='button' onClick={handleSave} className={SECONDARY_BUTTON_SMALL_CLASS}>
              Save
            </button>
            <button
              type='button'
              onClick={handleSubmitClick}
              disabled={isSubmitDisabled}
              className={PRIMARY_BUTTON_SMALL_CLASS}
            >
              {isSubmitting ? 'Submitting…' : 'Submit Attendance'}
            </button>
          </footer>
        </>
      )}

      {/* Confirm submit overlay */}
      {showConfirmOverlay && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='confirm-overlay-title'
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'
        >
          <div className='bg-white rounded-xl shadow-xl px-6 py-6 max-w-md w-full mx-4'>
            <h2 id='confirm-overlay-title' className='text-[2rem] leading-tight font-bold text-gray-900 mb-3'>
              Confirm Attendance
            </h2>
            <p className='text-gray-600 text-[1.6rem] leading-relaxed mb-5'>
              <span className='font-semibold text-blue-700'>{attendanceCounts.present}</span> student
              {attendanceCounts.present !== 1 ? 's' : ''} present,{' '}
              <span className='font-semibold text-yellow-600'>{attendanceCounts.absent}</span> student
              {attendanceCounts.absent !== 1 ? 's' : ''} absent.
            </p>
            <div className='flex gap-3 justify-end'>
              <button type='button' onClick={handleConfirmCancel} className={SECONDARY_BUTTON_SMALL_CLASS}>
                Cancel
              </button>
              <button type='button' onClick={handleConfirmSubmit} className={PRIMARY_BUTTON_SMALL_CLASS}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result overlay — success or error */}
      {showResultOverlay && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='result-overlay-title'
          className={[
            'fixed inset-0 z-50 flex items-center justify-center',
            submissionError ? 'bg-black/50' : 'bg-black/30',
          ].join(' ')}
        >
          <div className='bg-white rounded-xl shadow-xl p-8 max-w-sm w-full mx-4'>
            {submissionError ? (
              <>
                <h2 id='result-overlay-title' className='text-[2.5rem] font-bold text-red-700 mb-2'>
                  Submission Failed
                </h2>
                <p className='text-gray-600 text-[1.75rem] mb-6'>
                  The attendance couldn&rsquo;t be submitted. Please try again.
                </p>
                <div className='flex justify-end'>
                  <button type='button' onClick={handleResultOkay} className={PRIMARY_BUTTON_CLASS}>
                    Okay
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id='result-overlay-title' className='text-[2.5rem] font-bold text-green-700 mb-2'>
                  Attendance Submitted
                </h2>
                <p className='text-gray-500 text-[1.75rem]'>Redirecting you back to the unit…</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TakweenAttendancePage;
